import { KatToast } from './toast';

interface ManagedToast extends KatToast {
  next?: ManagedToast;
  prev?: ManagedToast;
}

interface ExtendedHTMLDivElement extends HTMLDivElement {
  prev?: ManagedToast | ExtendedHTMLDivElement;
  next?: ManagedToast | ExtendedHTMLDivElement;
}

/** ToastManager needs to look up Toasts by id but it also needs to be able to re-order the toasts so a regular Map
 * won't do. The doubly-linked list combined with the Map can do both things more efficiently (time-wise) than an array
 * for only a small cost of additional 'space'.*/
type LinkedToast = ManagedToast | ExtendedHTMLDivElement;

/**
 * ToastManager reduces the complexity of ToastGroup by handling the logic of linking toasts and changing their order.
 * ToastGroup's responsibility is to be the receptor of `toasts` and handles their rendering and related events.
 * By keeping these two roles separate, future revisions to how toasts work can be made with less rework. Testing is
 * also less of a burden as ToastManager is not a lit component.
 */
export class ToastManager {
  private head?: LinkedToast | null;
  private tail?: LinkedToast | null;
  private toastMap: Map<string, LinkedToast>;
  constructor() {
    this.head = null;
    this.tail = null;
    this.toastMap = new Map();
  }

  getToast(id: string): KatToast {
    return this.toastMap.get(id) as KatToast;
  }

  getToasts() {
    const orderedToasts = [];
    let currentToast = this.tail;

    while (currentToast) {
      orderedToasts.push(currentToast);
      currentToast = currentToast.prev;
    }

    return orderedToasts;
  }

  addToast(toast: LinkedToast) {
    this.convertIdToString(toast);
    this._appendToTail(toast);
  }

  private convertIdToString(toast: ManagedToast | ExtendedHTMLDivElement) {
    if (toast.id !== null && toast.id !== undefined) {
      if (!(typeof toast.id === 'string')) {
        toast.id = String(toast.id);
      }
    }
  }

  private _appendToTail(expandingDiv: ManagedToast | ExtendedHTMLDivElement) {
    if (expandingDiv.id && !this.toastMap.get(expandingDiv.id)) {
      this.toastMap.set(expandingDiv.id, expandingDiv);

      if (!this.head) {
        this.head = expandingDiv;
        this.tail = expandingDiv;
      } else {
        expandingDiv.prev = this.tail;
        this.tail.next = expandingDiv;
        this.tail = expandingDiv;
      }
    }
  }

  private swapToasts(originalId, replacement) {
    const original = this.toastMap.get(originalId);
    if (original) {
      replacement.prev = original.prev;
      replacement.next = original.next;
      this.head = this.head === original ? replacement : this.head;
      this.tail = this.tail === original ? replacement : this.tail;
      if (original?.prev?.next) {
        original.prev.next = replacement;
      }
      if (original?.next?.prev) {
        original.next.prev = replacement;
      }
      this.toastMap.set(originalId, replacement);
    }
  }

  private _createLinkableDiv(clazzName: string, toastId: string) {
    const linkable = document.createElement('div') as LinkedToast;
    linkable.id = toastId + '-filler' + this.getToasts().length;
    linkable.classList.add(clazzName);
    return linkable;
  }

  public removeToast(id: string): Promise<string> {
    const shrinkingToast = this._createLinkableDiv('shrinking-spacer', id);
    this.swapToasts(id, shrinkingToast);
    return new Promise<string>(resolve => {
      requestAnimationFrame(() => {
        shrinkingToast.addEventListener(
          'animationend',
          (event: AnimationEvent) => {
            if (event.animationName === 'shrink') {
              this._removeFiller(id);
              resolve(id);
            }
          },
          { once: true }
        );
      });
    });
  }

  private _removeFiller(id) {
    const filler = this.toastMap.get(id);
    if (filler) {
      if (filler === this.head) {
        this.head = filler.next;
      }
      if (filler === this.tail) {
        this.tail = filler.prev;
      }
      if (filler.prev) {
        filler.prev.next = filler.next;
      }
      if (filler.next) {
        filler.next.prev = filler.prev;
      }
      this.toastMap.delete(id);
    }
  }

  public hasToast(toast: KatToast | string) {
    if (typeof toast === 'string') {
      return toast && toast in this.toastMap;
    }
    if (toast instanceof KatToast) {
      return toast.id && this.toastMap.has(toast.id);
    }
  }
}
